/**
 * e-Tesa — Dialoguri auxiliare pentru editorul Deviz.
 * Extras din DevizTab.jsx (refactor Etapa 1C).
 *
 * Toate componentele sunt prezentaționale: primesc state-ul + callback-uri.
 */

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Link, Loader2, FileText, Target, Calculator, ShoppingCart, Search, Package, AlertTriangle, Check, Plus } from "lucide-react";

// ─── Selector Contract ───────────────────────────────────────────────────────
export function ContractSelectorDialog({ open, onOpenChange, loadingContracts, contractsList, onSelect }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[70vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link className="w-5 h-5" />
            Preia date din Contract
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[50vh] overflow-auto">
          {loadingContracts ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              <span className="ml-2">Se încarcă contractele...</span>
            </div>
          ) : contractsList.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nu există contracte disponibile</p>
              <p className="text-sm">Creați un contract mai întâi</p>
            </div>
          ) : (
            contractsList.map((contract) => (
              <div
                key={contract.id}
                onClick={() => onSelect(contract)}
                className="p-3 border rounded-lg cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
              >
                <div className="font-semibold text-blue-700">
                  CTR {contract.contract_number || "?"} din {contract.contract_date || "?"}
                </div>
                <div className="text-sm text-slate-600">
                  <strong>Client:</strong> {contract.beneficiar_name || "-"}
                </div>
                <div className="text-sm text-slate-500 truncate">
                  <strong>Obiect:</strong> {contract.obiect_lucrare || "-"}
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Calcul Invers (ajustare cheltuieli indirecte din preț țintă) ────────────
export function InverseCalcDialog({ open, onOpenChange, currentPriceFormatted, targetPrice, setTargetPrice, onCalculate, mode = "indirecte", setMode }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-green-600" />
            Ajustare Deviz la Preț Țintă
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Preț actual (cu TVA)</Label>
            <Input value={currentPriceFormatted} disabled className="bg-slate-100" />
          </div>
          <div>
            <Label>Preț țintă dorit (cu TVA) *</Label>
            <Input
              type="number"
              step="0.01"
              value={targetPrice}
              onChange={(e) => setTargetPrice(e.target.value)}
              placeholder="Ex: 15000"
              className="text-lg font-semibold"
              autoFocus
            />
          </div>
          <div>
            <Label className="mb-2 block">Cum să ajustez?</Label>
            <div className="space-y-2" data-testid="inverse-calc-modes">
              <label className={`flex items-start gap-2 p-2.5 rounded-md border cursor-pointer transition ${mode === "indirecte" ? "border-green-500 bg-green-50" : "border-slate-200 hover:bg-slate-50"}`}>
                <input type="radio" checked={mode === "indirecte"} onChange={() => setMode && setMode("indirecte")} className="mt-0.5" />
                <div className="text-xs">
                  <div className="font-semibold text-slate-800">Ajustează cheltuielile indirecte %</div>
                  <div className="text-slate-500">Păstrează cantități și prețuri unitare. Recomandat când vrei să lași prețurile din catalog neschimbate.</div>
                </div>
              </label>
              <label className={`flex items-start gap-2 p-2.5 rounded-md border cursor-pointer transition ${mode === "preturi" ? "border-green-500 bg-green-50" : "border-slate-200 hover:bg-slate-50"}`}>
                <input type="radio" checked={mode === "preturi"} onChange={() => setMode && setMode("preturi")} className="mt-0.5" />
                <div className="text-xs">
                  <div className="font-semibold text-slate-800">Scalează prețurile unitare</div>
                  <div className="text-slate-500">Materialele și manopera se multiplică proporțional. Păstrează cantitățile și procentele.</div>
                </div>
              </label>
              <label className={`flex items-start gap-2 p-2.5 rounded-md border cursor-pointer transition ${mode === "cantitati" ? "border-green-500 bg-green-50" : "border-slate-200 hover:bg-slate-50"}`}>
                <input type="radio" checked={mode === "cantitati"} onChange={() => setMode && setMode("cantitati")} className="mt-0.5" />
                <div className="text-xs">
                  <div className="font-semibold text-slate-800">Scalează cantitățile</div>
                  <div className="text-slate-500">Toate cantitățile se multiplică cu același factor. Păstrează prețurile unitare din catalog.</div>
                </div>
              </label>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => { onOpenChange(false); setTargetPrice(""); }}
              className="flex-1"
            >
              Anulează
            </Button>
            <Button onClick={onCalculate} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="inverse-calc-apply-btn">
              <Calculator className="w-4 h-4 mr-2" />
              Aplică
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Editor descriere capitol de lucrări ────────────────────────────────────
export function EditItemDialog({ open, editingItem, editingName, setEditingName, onCancel, onSave }) {
  return (
    <Dialog open={!!open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Editare Capitol de Lucrări</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Descrierea completă a lucrării</Label>
            <textarea
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              className="w-full min-h-[150px] p-3 border rounded-lg mt-2 text-sm resize-y focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Introduceți descrierea completă a lucrării..."
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onCancel}>Anulează</Button>
            <Button
              className="bg-teal-600 hover:bg-teal-700"
              onClick={() => editingItem && onSave(editingItem.id, editingName)}
            >
              Salvează
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Selector Materiale din Inventar ────────────────────────────────────────
export function MaterialsPickerDialog({
  open,
  onOpenChange,
  loadingInventory,
  filteredInventory,
  assignedMaterials,
  materialsSearchTerm,
  setMaterialsSearchTerm,
  onAddMaterial,
  formatNumber,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-amber-600" />
            Adaugă Materiale din Inventar
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Caută în inventar..."
              value={materialsSearchTerm}
              onChange={(e) => setMaterialsSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <ScrollArea className="h-[350px] border rounded-lg p-2">
            {loadingInventory ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
              </div>
            ) : filteredInventory.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Package className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p>Nu s-au găsit produse</p>
                <p className="text-xs">Adăugați produse din tab-ul Inventar</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredInventory.map((item) => {
                  const isAdded = assignedMaterials.some((m) => m.item_id === item.id);
                  const lowStock = item.stock <= 5;
                  return (
                    <div
                      key={item.id}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                        isAdded ? "bg-green-50 border-green-200" : "bg-white hover:bg-slate-50"
                      } ${lowStock ? "border-amber-300" : ""}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{item.name}</p>
                          {lowStock && (
                            <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Stoc scăzut
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500">
                          {formatNumber(item.material_price)} RON/{item.um} | Stoc: {item.stock} {item.um}
                          {item.supplier && ` | ${item.supplier}`}
                        </p>
                      </div>
                      <Button
                        variant={isAdded ? "secondary" : "default"}
                        size="sm"
                        onClick={() => onAddMaterial(item)}
                        disabled={isAdded}
                        className={isAdded ? "bg-green-100 text-green-700" : "bg-amber-600 hover:bg-amber-700"}
                      >
                        {isAdded ? (
                          <><Check className="w-4 h-4 mr-1" />Adăugat</>
                        ) : (
                          <><Plus className="w-4 h-4 mr-1" />Adaugă</>
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>

          <div className="flex items-center justify-between pt-2 border-t">
            <p className="text-sm text-slate-600">{assignedMaterials.length} material(e) selectate</p>
            <Button onClick={() => onOpenChange(false)} className="bg-amber-600 hover:bg-amber-700">
              Finalizează
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
